import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Unplug } from "lucide-react";
import { STRAVA_RECONNECT_BANNER } from "@atlas/shared";
import { fetchIntegrations } from "../api/integrations";
import { Button } from "./Button";

// ─── Strava needs a reconnect ────────────────────────────────────────────────
// App-wide strip (mounted once in AppShell, beside ImportProgressBanner) while
// Strava refuses the connection (IntegrationRow.needsReconnect, migration
// 0073). Without it the only sign was a red line on Connected sources, and a
// new account just sat empty. Hidden on Connected sources itself, which
// carries the full prompt and the official Connect with Strava button.
// Calm: neutral surface, coral icon, secondary button. Not gold.

const STALE_MS = 5 * 60 * 1000;

export function StravaReconnectBanner() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Same key as Connected sources, so a reconnect there clears this too.
  const { data } = useQuery({
    queryKey: ["integrations"],
    queryFn: fetchIntegrations,
    staleTime: STALE_MS,
  });

  const strava = data?.integrations.find((r) => r.provider === "strava");
  if (!strava?.connected || strava.needsReconnect !== true) return null;
  if (pathname.startsWith("/connected-sources")) return null;

  return (
    <div
      role="region"
      aria-label="Strava connection"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
        flexWrap: "wrap",
        padding: "10px 16px",
        background: "var(--surface-card)",
        borderBottom: "1px solid var(--border-subtle)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <Unplug size={15} style={{ color: "var(--status-danger)", flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: "1 1 240px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.4 }}>
          {STRAVA_RECONNECT_BANNER.title}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45, marginTop: 2 }}>
          {STRAVA_RECONNECT_BANNER.detail}
        </div>
      </div>
      <Button variant="secondary" size="sm" onClick={() => void navigate({ to: "/connected-sources" })}>
        {STRAVA_RECONNECT_BANNER.action}
      </Button>
    </div>
  );
}
